import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase-client';
const isPendingRoleRequestStatus = status => {
  const normalized = String(status || '').trim().toLowerCase();
  return !normalized || normalized === 'requested' || normalized === 'pending';
};
const toMillis = value => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};
function parseOccurrenceId(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}
function splitCompoundId(value) {
  if (typeof value !== 'string' || !value.includes('__')) {
    return {
      baseId: value || '',
      occurrenceId: null
    };
  }
  const parts = value.split('__').filter(Boolean);
  if (!parts.length) return {
    baseId: value,
    occurrenceId: null
  };
  const occurrenceId = parts.find(part => parseOccurrenceId(part)) || null;
  return {
    baseId: parts[0] || value,
    occurrenceId
  };
}
function getCalendarTarget(eventDocId, eventData) {
  const explicitOriginal = typeof eventData?.originalEventId === 'string' ? eventData.originalEventId.trim() : '';
  const explicitOccurrence = typeof eventData?.occurrenceId === 'string' ? eventData.occurrenceId.trim() : '';
  const isOccurrenceDoc = eventData?.scope === 'occurrence' || !!explicitOriginal || !!explicitOccurrence || String(eventDocId || '').includes('__');
  if (!isOccurrenceDoc) {
    return {
      calendarEventId: eventDocId,
      occurrenceId: null,
      scope: 'event'
    };
  }
  const calendarEventId = splitCompoundId(explicitOriginal || eventDocId).baseId || eventDocId;
  const occurrenceId = parseOccurrenceId(explicitOccurrence) || splitCompoundId(eventDocId).occurrenceId || null;
  return {
    calendarEventId,
    occurrenceId,
    scope: 'occurrence'
  };
}
export function usePendingRoleRequests(teamId, enabled = true) {
  const [pendingRoleRequests, setPendingRoleRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!teamId || !enabled) {
      setPendingRoleRequests([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchPendingRoleRequests = async ({
      showLoader = false
    } = {}) => {
      if (showLoader && !cancelled) setLoading(true);
      try {
        const eventsSnap = await getDocs(collection(db, 'teams', teamId, 'events'));
        const perEvent = await Promise.all(eventsSnap.docs.map(async eventDoc => {
          const eventData = eventDoc.data() || {};
          const eventName = typeof eventData?.name === 'string' && eventData.name.trim() !== '' ? eventData.name.trim() : 'Event';
          const calendarTarget = getCalendarTarget(eventDoc.id, eventData);
          try {
            const roleRequestsSnap = await getDocs(collection(db, 'teams', teamId, 'events', eventDoc.id, 'roleRequests'));
            return roleRequestsSnap.docs.map(requestDoc => {
              const data = requestDoc.data() || {};
              if (!isPendingRoleRequestStatus(data.status)) return null;
              return {
                id: `${eventDoc.id}:${requestDoc.id}`,
                requestId: requestDoc.id,
                eventDocId: eventDoc.id,
                eventName,
                roleName: typeof data.roleName === 'string' && data.roleName.trim() !== '' ? data.roleName.trim() : 'Role',
                requestedBy: typeof data.requestedBy === 'string' ? data.requestedBy : '',
                requestedByName: typeof data.requestedByName === 'string' && data.requestedByName.trim() !== '' ? data.requestedByName.trim() : typeof data.requestedBy === 'string' ? data.requestedBy : 'Member',
                slotId: typeof data.slotId === 'string' && data.slotId.trim() !== '' ? data.slotId.trim() : '',
                status: typeof data.status === 'string' && data.status.trim() !== '' ? data.status.trim() : 'requested',
                calendarEventId: calendarTarget.calendarEventId,
                occurrenceId: calendarTarget.occurrenceId,
                scope: calendarTarget.scope,
                sortTs: toMillis(data.updatedAt || data.createdAt)
              };
            }).filter(Boolean);
          } catch (err) {
            if (String(err?.code || '') !== 'permission-denied') {}
            return [];
          }
        }));
        if (cancelled) return;
        const flat = perEvent.flat().sort((a, b) => {
          const delta = (b.sortTs || 0) - (a.sortTs || 0);
          if (delta !== 0) return delta;
          return String(a.id).localeCompare(String(b.id));
        });
        setPendingRoleRequests(flat);
      } catch (err) {
        if (String(err?.code || '') !== 'permission-denied') {}
        if (!cancelled) setPendingRoleRequests([]);
      } finally {
        if (showLoader && !cancelled) setLoading(false);
      }
    };
    fetchPendingRoleRequests({
      showLoader: true
    });
    const intervalId = window.setInterval(() => {
      fetchPendingRoleRequests();
    }, 30000);
    const onFocus = () => {
      fetchPendingRoleRequests();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [teamId, enabled]);
  return {
    pendingRoleRequests,
    loading
  };
}
